import { API_BASE_URL } from '../config/apiConfig';

// Helper function for API calls
const apiCall = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
  const config = {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  try {
    const response = await fetch(url, config);
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'API request failed');
    }
    
    return data;
  } catch (error) {
    console.error(`API Error (${endpoint}):`, error);
    throw error;
  }
};

// Authentication API
export const authAPI = {
  login: (credentials) => apiCall('/auth/login', {
    method: 'POST',
    body: JSON.stringify(credentials),
  }),
  verify: (token) => apiCall('/auth/verify', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  }),
  logout: () => apiCall('/auth/logout', { method: 'POST' }),
};

// About API
export const aboutAPI = {
  get: () => apiCall('/about'),
  update: (data) => apiCall('/about', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// Skills API
export const skillsAPI = {
  getAll: (all = false) => apiCall(`/skills${all ? '?all=true' : ''}`),
  getByCategory: (category) => apiCall(`/skills/category/${category}`),
  add: (skill) => apiCall('/skills', {
    method: 'POST',
    body: JSON.stringify(skill),
  }),
  update: (id, skill) => apiCall(`/skills/${id}`, {
    method: 'PUT',
    body: JSON.stringify(skill),
  }),
  delete: (id) => apiCall(`/skills/${id}`, { method: 'DELETE' }),
};

// Projects API
export const projectsAPI = {
  getAll: (all = false) => apiCall(`/projects${all ? '?all=true' : ''}`),
  getById: (id) => apiCall(`/projects/${id}`),
  add: (project) => apiCall('/projects', {
    method: 'POST',
    body: JSON.stringify(project),
  }),
  update: (id, project) => apiCall(`/projects/${id}`, {
    method: 'PUT',
    body: JSON.stringify(project),
  }),
  delete: (id) => apiCall(`/projects/${id}`, { method: 'DELETE' }),
};

// Experience API
export const experienceAPI = {
  getAll: () => apiCall('/experience'),
  getById: (id) => apiCall(`/experience/${id}`),
  add: (experience) => apiCall('/experience', {
    method: 'POST',
    body: JSON.stringify(experience),
  }),
  update: (id, experience) => apiCall(`/experience/${id}`, {
    method: 'PUT',
    body: JSON.stringify(experience),
  }),
  delete: (id) => apiCall(`/experience/${id}`, { method: 'DELETE' }),
};

// Sections API
export const sectionsAPI = {
  getAll: () => apiCall('/sections'),
  getByName: (sectionName) => apiCall(`/sections/${sectionName}`),
  add: (section) => apiCall('/sections', {
    method: 'POST',
    body: JSON.stringify(section),
  }),
  update: (id, section) => apiCall(`/sections/${id}`, {
    method: 'PUT',
    body: JSON.stringify(section),
  }),
  delete: (id) => apiCall(`/sections/${id}`, { method: 'DELETE' }),
};

// Messages API
export const messagesAPI = {
  getAll: () => apiCall('/messages'),
  getById: (id) => apiCall(`/messages/${id}`),
  submit: (message) => apiCall('/messages', {
    method: 'POST',
    body: JSON.stringify(message),
  }),
  delete: (id) => apiCall(`/messages/${id}`, { method: 'DELETE' }),
};

// Upload API
export const uploadAPI = {
  uploadImage: (file, token) => {
    const formData = new FormData();
    formData.append('image', file);
    
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    
    return fetch(`${API_BASE_URL}/upload`, {
      method: 'POST',
      headers,
      body: formData,
    }).then(res => res.json());
  },
  getAllImages: () => apiCall('/upload'),
  deleteImage: (id) => apiCall(`/upload/${id}`, { method: 'DELETE' }),
};

// Patents API
export const patentsAPI = {
  getAll: () => apiCall('/patents'),
  getById: (id) => apiCall(`/patents/${id}`),
  add: (patent) => apiCall('/patents', {
    method: 'POST',
    body: JSON.stringify(patent),
  }),
  update: (id, patent) => apiCall(`/patents/${id}`, {
    method: 'PUT',
    body: JSON.stringify(patent),
  }),
  delete: (id) => apiCall(`/patents/${id}`, { method: 'DELETE' }),
};

// Health check
export const healthCheck = () => apiCall('/health');

export default {
  auth: authAPI,
  about: aboutAPI,
  skills: skillsAPI,
  projects: projectsAPI,
  experience: experienceAPI,
  sections: sectionsAPI,
  messages: messagesAPI,
  upload: uploadAPI,
  patents: patentsAPI,
  health: healthCheck,
  // Thin HTTP wrappers for admin endpoints migration
  get: (path) => apiCall(path, { method: 'GET' }),
  post: (path, body) => apiCall(path, { method: 'POST', body: JSON.stringify(body) }),
  put: (path, body) => apiCall(path, { method: 'PUT', body: JSON.stringify(body) }),
  del: (path) => apiCall(path, { method: 'DELETE' }),
}; 